import React from 'react';
import {
  Snackbar,
  Alert,
  LinearProgress,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useEligibilityRefresh } from '../hooks/useEligibilityRefresh';

/**
 * Global progress indicator for eligibility refresh operations
 * Shows as a snackbar with progress bar when refresh is active
 */
const EligibilityRefreshProgress: React.FC = () => {
  const {
    status,
    isActive,
    isCompleted,
    hasError,
    progress,
    statusMessage,
    cancelRefresh,
  } = useEligibilityRefresh();

  // Track if we've shown a completion message in this session
  const [hasShownCompletion, setHasShownCompletion] = React.useState(false);
  const [showCompleted, setShowCompleted] = React.useState(false);
  const [wasActiveInSession, setWasActiveInSession] = React.useState(false);
  
  // Track when calculation becomes active in this session
  React.useEffect(() => {
    if (isActive && !wasActiveInSession) {
      setWasActiveInSession(true);
    }
  }, [isActive, wasActiveInSession]);
  
  React.useEffect(() => {
    if (isCompleted && !hasShownCompletion && wasActiveInSession) {
      // Only show completion message if:
      // 1. We haven't shown one in this session
      // 2. A calculation was actually active in this session (not just detected on load)
      setShowCompleted(true);
      setHasShownCompletion(true);
      // Auto-hide completed message after 5 seconds
      const timer = setTimeout(() => {
        setShowCompleted(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else if (!isCompleted) {
      setShowCompleted(false);
    }
  }, [isCompleted, hasShownCompletion, wasActiveInSession]);
  
  if (!isActive && !showCompleted && !hasError) {
    return null;
  }

  const handleClose = () => {
    if (isActive) {
      cancelRefresh();
    }
  };

  const getSeverity = () => {
    if (hasError) return 'error';
    if (isCompleted) return 'success';
    return 'info';
  };

  const getMessage = () => {
    if (hasError) {
      return status.error || 'Eligibility calculation failed';
    }
    if (isCompleted) {
      return 'Eligibility calculation completed!';
    }
    return statusMessage;
  };

  return (
    <Snackbar
      open={isActive || isCompleted || hasError}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ mb: 2 }}
    >
      <Alert
        severity={getSeverity()}
        onClose={isActive ? handleClose : undefined}
        sx={{ minWidth: 300 }}
        action={
          isActive ? (
            <IconButton
              size="small"
              aria-label="cancel"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined
        }
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="body2" sx={{ mb: isActive ? 1 : 0 }}>
            {getMessage()}
          </Typography>
          
          {isActive && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {progress}% complete
              </Typography>
            </Box>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default EligibilityRefreshProgress;
